// Contact.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import emailjs from "emailjs-com";
import "./Contact.css";

const LEVEL = [
  "###############",
  "#.............#",
  "#.###.###.###.#",
  "#.............#",
  "###.#.###.#.###",
  "#...#.....#...#",
  "#.#####.#####.#",
  "#.............#",
  "###############",
];

function buildLevel() {
  const rows = LEVEL.length;
  const cols = LEVEL[0].length;
  const walls = new Set();
  const pellets = new Set();

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const ch = LEVEL[r][c];
      const key = `${r},${c}`;
      if (ch === "#") walls.add(key);
      if (ch === ".") pellets.add(key);
    }
  }

  return { rows, cols, walls, pellets };
}

function dirToAngle(dir) {
  if (dir === "RIGHT") return 0;
  if (dir === "DOWN") return 90;
  if (dir === "LEFT") return 180;
  if (dir === "UP") return 270;
  return 0;
}

function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const CONTACT_EMAIL = "khaledasad94@outlook.com";
  const LINKEDIN_URL = "https://www.linkedin.com/in/khaled-asad/";

  // ---- Arcade game state ----
  const level = useMemo(() => buildLevel(), []);
  const [coinState, setCoinState] = useState("idle"); // idle | inserting | playing
  const [started, setStarted] = useState(false);
  const [won, setWon] = useState(false);
  const [lost, setLost] = useState(false);

  const [player, setPlayer] = useState({ r: 1, c: 1 });
  const [ghost, setGhost] = useState({ r: level.rows - 2, c: level.cols - 2 });
  const [pellets, setPellets] = useState(() => new Set(level.pellets));
  const [score, setScore] = useState(0);

  const directionRef = useRef("RIGHT");
  const ghostDirRef = useRef("LEFT");
  const [playerDir, setPlayerDir] = useState("RIGHT");

  const confettiPieces = useMemo(() => {
    const pieces = [];
    for (let i = 0; i < 90; i++) {
      pieces.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 2.6 + Math.random() * 1.6,
        rotate: Math.random() * 360,
        size: 6 + Math.random() * 8,
      });
    }
    return pieces;
  }, []);

  const initGame = useCallback(() => {
    setWon(false);
    setLost(false);
    setPlayer({ r: 1, c: 1 });
    setGhost({ r: level.rows - 2, c: level.cols - 2 });
    setPellets(new Set(level.pellets));
    setScore(0);
    directionRef.current = "RIGHT";
    ghostDirRef.current = "LEFT";
    setPlayerDir("RIGHT");
  }, [level]);

  const resetToCoin = useCallback(() => {
    setStarted(false);
    setCoinState("idle");
    initGame();
  }, [initGame]);

  const insertCoinAndStart = useCallback(() => {
    if (coinState !== "idle") return;

    initGame();
    setCoinState("inserting");

    setTimeout(() => {
      setCoinState("playing");
      setStarted(true);
    }, 850);
  }, [coinState, initGame]);

  const playAgainInstant = useCallback(() => {
    initGame();
    setCoinState("playing");
    setStarted(true);
  }, [initGame]);

  const isWall = useCallback((r, c) => level.walls.has(`${r},${c}`), [level]);

  const tryMove = useCallback(
    (pos, dir) => {
      let { r, c } = pos;
      if (dir === "UP") r -= 1;
      if (dir === "DOWN") r += 1;
      if (dir === "LEFT") c -= 1;
      if (dir === "RIGHT") c += 1;

      if (isWall(r, c)) return pos;
      return { r, c };
    },
    [isWall]
  );

  // stop page scroll while playing (arrow keys)
  useEffect(() => {
    if (!started || won || lost) return;

    const onKeyDown = (e) => {
      const tag = e.target?.tagName ? e.target.tagName.toLowerCase() : "";
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;

      const isArrow =
        e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight";
      if (isArrow) e.preventDefault();

      if (e.key === "ArrowUp") {
        directionRef.current = "UP";
        setPlayerDir("UP");
      }
      if (e.key === "ArrowDown") {
        directionRef.current = "DOWN";
        setPlayerDir("DOWN");
      }
      if (e.key === "ArrowLeft") {
        directionRef.current = "LEFT";
        setPlayerDir("LEFT");
      }
      if (e.key === "ArrowRight") {
        directionRef.current = "RIGHT";
        setPlayerDir("RIGHT");
      }
    };

    window.addEventListener("keydown", onKeyDown, { passive: false });
    return () => window.removeEventListener("keydown", onKeyDown, { passive: false });
  }, [started, won, lost]);

  // player tick
  useEffect(() => {
    if (!started || won || lost) return;

    const id = setInterval(() => {
      setPlayer((p) => {
        const next = tryMove(p, directionRef.current);

        const k = `${next.r},${next.c}`;
        setPellets((prev) => {
          if (!prev.has(k)) return prev;
          const copy = new Set(prev);
          copy.delete(k);
          setScore((s) => s + 10);
          if (copy.size === 0) setWon(true);
          return copy;
        });

        return next;
      });
    }, 280);

    return () => clearInterval(id);
  }, [started, won, lost, tryMove]);

  // ghost tick
  useEffect(() => {
    if (!started || won || lost) return;

    const dirs = ["UP", "DOWN", "LEFT", "RIGHT"];

    const id = setInterval(() => {
      setGhost((g) => {
        const chase = Math.random() < 0.38;

        let bestDir = ghostDirRef.current;
        let bestDist = Infinity;

        const options = dirs
          .map((d) => ({ d, pos: tryMove(g, d) }))
          .filter((x) => !(x.pos.r === g.r && x.pos.c === g.c));

        if (options.length === 0) return g;

        if (chase) {
          for (const opt of options) {
            const dist = Math.abs(opt.pos.r - player.r) + Math.abs(opt.pos.c - player.c);
            if (dist < bestDist) {
              bestDist = dist;
              bestDir = opt.d;
            }
          }
        } else {
          const canContinue = options.some((o) => o.d === ghostDirRef.current);
          bestDir = canContinue ? ghostDirRef.current : options[Math.floor(Math.random() * options.length)].d;
        }

        ghostDirRef.current = bestDir;
        return tryMove(g, bestDir);
      });
    }, 215);

    return () => clearInterval(id);
  }, [started, won, lost, tryMove, player]);

  // collision
  useEffect(() => {
    if (!started || won || lost) return;
    if (player.r === ghost.r && player.c === ghost.c) setLost(true);
  }, [player, ghost, started, won, lost]);

  // ---- contact form logic ----
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();

    emailjs
      .send("service_ra96bsr", "template_8wfjazv", formData, "ZxNCbfGTdfZ9e4zZs")
      .then(() => {
        setSubmitted(true);
        setError("");
        setFormData({ name: "", email: "", message: "" });
      })
      .catch(() => {
        setError("There was a problem submitting your form. Please try again.");
        setSubmitted(false);
      });
  };

  const handleDownloadResume = () => {
    const resumeFile = "https://khaledasad-bucket.s3.amazonaws.com/Khaled_Asad_Resume.pdf";
    window.open(resumeFile, "_blank");
  };

  return (
    <>
      <Helmet>
        <title>Contact</title>
        <meta name="description" content="Send me a message or connect on LinkedIn." />
      </Helmet>

      {won && (
        <div className="confettiOverlay" aria-hidden="true">
          {confettiPieces.map((p) => (
            <span
              key={p.id}
              className="confettiPiece"
              style={{
                left: `${p.left}%`,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
                transform: `rotate(${p.rotate}deg)`,
                width: `${p.size}px`,
                height: `${p.size * 0.6}px`,
              }}
            />
          ))}
          <div className="confettiWin">
            <div className="confettiWinTitle">You won! Yay!</div>
            <button type="button" className="button-primary confettiWinBtn" onClick={playAgainInstant}>
              Play Again
            </button>
          </div>
        </div>
      )}

      <div className="page-shell contactPage">
        <div className="cp-panel contactPanel">
          <div className="cp-panel__inner">
            <header className="contactHeader">
              <div className="contactHeaderText">
                <h1 className="cp-title glitch">Contact</h1>
                <p className="cp-subtitle">
                  Send a message below or reach out on LinkedIn. I would love to hear what you are building 🚀
                </p>
              </div>

              <div className="contactHeaderActions">
                <button className="button-secondary contactResumeBtn" type="button" onClick={handleDownloadResume}>
                  Download Resume
                </button>
              </div>
            </header>

            <div className="cp-divider contactDivider" />

            <div className="contactGrid">
              {/* Form */}
              <section className="cp-card contactFormCard" aria-label="Contact form">
                <div className="contactFormTop">
                  <div className="contactFormHint">All fields are required.</div>
                </div>

                {submitted && (
                  <div className="contactAlert contactAlert--success" role="status">
                    Thank you for your message. I will get back to you soon.
                  </div>
                )}

                {error && (
                  <div className="contactAlert contactAlert--error" role="alert">
                    {error}
                  </div>
                )}

                <form className="contactForm" onSubmit={handleSubmit}>
                  <div className="contactField">
                    <label className="contactLabel" htmlFor="contact-name">
                      Name
                    </label>
                    <input
                      id="contact-name"
                      className="contactInput"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      autoComplete="name"
                      required
                    />
                  </div>

                  <div className="contactField">
                    <label className="contactLabel" htmlFor="contact-email">
                      Email
                    </label>
                    <input
                      id="contact-email"
                      className="contactInput"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      autoComplete="email"
                      required
                    />
                  </div>

                  <div className="contactField contactField--message">
                    <label className="contactLabel" htmlFor="contact-message">
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      className="contactTextarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can I help"
                      required
                    />
                  </div>

                  <div className="contactActions">
                    <button className="button-primary contactSendBtn" type="submit">
                      Send Message
                    </button>

                    <a className="contactAltLink" href={`mailto:${CONTACT_EMAIL}`}>
                      Or email directly
                    </a>
                  </div>
                </form>
              </section>

              {/* Side */}
              <aside className="contactSide" aria-label="Contact links">
                <div className="cp-card contactSideCard">
                  <div className="contactSideTitle">Quick Links</div>

                  <div className="contactLinks">
                    <a className="contactLinkRow" href={`mailto:${CONTACT_EMAIL}`}>
                      <span className="contactIcon" aria-hidden="true">
                        ✉
                      </span>
                      <span className="contactLinkText">
                        <span className="contactLinkLabel">Email</span>
                        <span className="contactLinkValue">{CONTACT_EMAIL}</span>
                      </span>
                    </a>

                    <a className="contactLinkRow" href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
                      <span className="contactIcon" aria-hidden="true">
                        in
                      </span>
                      <span className="contactLinkText">
                        <span className="contactLinkLabel">LinkedIn</span>
                        <span className="contactLinkValue">khaled asad</span>
                      </span>
                    </a>
                  </div>

                  <div className="contactSideNote">
                    If you are reaching out about roles, feel free to include the job link and team details.
                  </div>
                </div>

                {/* Arcade Game */}
                <div className="cp-card contactSideCard contactGameCard" aria-label="Arcade game">
                  <div className="contactSideTitle">Arcade Game</div>

                  {coinState !== "playing" && (
                    <div className={["coinScreen", coinState === "inserting" ? "is-inserting" : ""].join(" ")}>
                      <div className="coinHeader">INSERT COIN</div>
                      <div className="coinSub">to play</div>

                      {coinState === "inserting" && (
                        <div className="coinBoot" aria-live="polite">
                          <div className="coinBootText">Booting…</div>
                          <div className="coinBootBar" aria-hidden="true">
                            <span className="coinBootFill" />
                          </div>
                        </div>
                      )}

                      <div className="coinRig">
                        <button
                          type="button"
                          className="coinBtn"
                          onClick={insertCoinAndStart}
                          aria-label="Insert coin to play"
                          disabled={coinState === "inserting"}
                        >
                          <span className="coin" aria-hidden="true" />
                        </button>

                        <div className="coinSlot" aria-hidden="true">
                          <span className="coinSlotLine" />
                          <span className="coinSlotGlow" />
                        </div>
                      </div>

                      <div className="coinHint">
                        Click the coin • then use <strong>arrow keys</strong>
                      </div>
                    </div>
                  )}

                  {coinState === "playing" && (
                    <>
                      <div className="gameTop">
                        <div className="gameMeta">
                          <span className="gameScoreLabel">Score</span>
                          <span className="gameScoreVal">{score}</span>
                        </div>

                        <div className="gameBtns">
                          <button type="button" className="button-secondary gameBtn" onClick={resetToCoin}>
                            Reset
                          </button>
                        </div>
                      </div>

                      <div className="gameHint">
                        Use <strong>arrow keys</strong> to move. Eat all dots to win.
                      </div>

                      <div
                        className="gameGrid"
                        style={{
                          gridTemplateColumns: `repeat(${level.cols}, var(--cell))`,
                          gridTemplateRows: `repeat(${level.rows}, var(--cell))`,
                        }}
                      >
                        {Array.from({ length: level.rows * level.cols }).map((_, i) => {
                          const r = Math.floor(i / level.cols);
                          const c = i % level.cols;
                          const key = `${r},${c}`;

                          const wall = level.walls.has(key);
                          const pellet = pellets.has(key);
                          const isPlayer = player.r === r && player.c === c;
                          const isGhost = ghost.r === r && ghost.c === c;

                          return (
                            <div
                              key={key}
                              className={[
                                "cell",
                                wall ? "cell--wall" : "cell--floor",
                                pellet ? "cell--pellet" : "",
                              ].join(" ")}
                            >
                              {isPlayer && (
                                <div
                                  className="entity pacWrap"
                                  aria-hidden="true"
                                  style={{ transform: `rotate(${dirToAngle(playerDir)}deg)` }}
                                >
                                  <div className="pacInner" />
                                </div>
                              )}

                              {isGhost && <div className="entity ghost" aria-hidden="true" />}
                            </div>
                          );
                        })}
                      </div>

                      {lost && !won && (
                        <div className="gameOverlay" role="status">
                          <div className="gameOverlayTitle">Game Over</div>
                          <button type="button" className="button-primary gameBtnPrimary" onClick={playAgainInstant}>
                            Try Again
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Contact;
